import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, ReactNode} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import FilterPillList from 'data-frames/filters/components/FilterPillList';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import DataFrameTable, {
  DataFrameRenderOverrides,
  DataFrameTableSelectionOptions,
} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import useDataFrameTableMode from 'data-frames/table/hooks/useDataFrameTableMode';
import useDataFrameTableSelectionState from 'data-frames/table/hooks/useDataFrameTableSelectionState';
import DataFrameActionToolbar, {PrimaryActionRenderFn} from 'data-frames/toolbar/DataFrameActionToolbar';
import {
  IS_ADD_TO_WORKLIST_ENABLED,
  renderAddToWorklistOption,
} from 'data-frames/toolbar/toolbar-actions/renderAddToWorklistOption';
import {getRenderClearFlagRowsButtonFn} from 'data-frames/toolbar/toolbar-actions/renderClearFlagRowsButton';
import {renderCopyToClipboardOption} from 'data-frames/toolbar/toolbar-actions/renderCopyToClipboardOption';
import {renderDownloadAsCSVOption} from 'data-frames/toolbar/toolbar-actions/renderDownloadAsCSVOption';
import {getRenderFlagRowsButtonFn} from 'data-frames/toolbar/toolbar-actions/renderFlagRowsButton';
import renderToggleChipsOption from 'data-frames/toolbar/toolbar-actions/renderToggleChipsOption';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import CollapserChevron from 'ui-library/CollapserChevron';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Option from 'ui-library/menus/Option';
import Pane from 'ui-library/pane/Pane';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';

const isExpandedGlobalClientState = makeUseGlobalClientState<boolean | undefined>(undefined);

export interface DataFrameTableWrapperOverflowOption {
  icon: BenchlingIconName;
  onSelect: () => void;
  title: string;
  isDisabled?: boolean;
  tooltipTitle?: string | null;
}

export interface RowSelectionProps {
  onSelectRows: (rowIds: Set<string>, isAddingFlag: boolean) => void;
  flaggedRows: Set<string>;
}

export interface DataFrameTableWrapperProps {
  /** IDs are optional, though should be specified when available for analytic event tracking purposes. */
  dataFrameId?: string;
  actionOrigin: ActionOrigin;
  rowSelectionProps?: RowSelectionProps;
  banner?: ReactNode;
  clientStateId: string;
  dataFrame: DataFrame;
  defaultTableMode?: DataFrameTableMode;
  renderIsExcludedColumn?: boolean;
  dataFrameRenderOverrides?: DataFrameRenderOverrides;
  /** Additional menu options to appear in the overflow menu, before the standard overflow menu options. */
  additionalOverflowOptions?: Array<DataFrameTableWrapperOverflowOption>;
  /** Additional elements to render in order before the overflow menu button.
   *
   * See `DataFrameActionToolbar`'s `renderElements`.
   */
  additionalElements?: Array<PrimaryActionRenderFn>;
  headerMenuOptions?: DataFrameHeaderMenuOptions;
  /**
   * The title to show in the pane; if not passed, defaults to the data frame's name
   */
  title?: React.ReactNode;
  isCollapsible?: boolean;
  autoCollapseRowLimit?: number;
}

/**
 * Render a `Pane` with a `DataFrame`'s title, contents, and a `DataFrameActionToolbar` with copy, download,
 * and toggle chips options.
 */
export default function DataFrameTableWrapper({
  dataFrameId,
  actionOrigin,
  banner,
  clientStateId,
  dataFrame,
  rowSelectionProps,
  defaultTableMode = DataFrameTableMode.TEXT,
  renderIsExcludedColumn = true,
  additionalOverflowOptions,
  additionalElements = [],
  headerMenuOptions,
  title,
  isCollapsible = false,
  dataFrameRenderOverrides,
  autoCollapseRowLimit,
}: DataFrameTableWrapperProps): JSX.Element {
  const sortOptions = useSimpleTableSortState(clientStateId);

  const [maybeIsExpanded, setIsExpanded] = isExpandedGlobalClientState.useGlobalClientState(clientStateId);
  const isInitiallyCollapsed = autoCollapseRowLimit && dataFrame.rows.length > autoCollapseRowLimit;
  const isExpanded = !isCollapsible || (maybeIsExpanded ?? !isInitiallyCollapsed);

  const {tableMode, setTableMode} = useDataFrameTableMode(clientStateId, defaultTableMode);

  /**
   * Use only DataFrameTableMode.TEXT for sorting to avoid a dependency on `DataFrame.hydratedProperties`,
   * so hydrated properties can be removed in the future.
   */
  const sortedDataFrame = useMemo(
    () => sortDataFrame(dataFrame, sortOptions.sortState, DataFrameTableMode.TEXT),
    [dataFrame, sortOptions.sortState]
  );

  const selectionStateHookOutput = useDataFrameTableSelectionState(clientStateId, sortedDataFrame);
  const numSelectedRows = selectionStateHookOutput.selectedRowIds.size;

  const selectionOptions: DataFrameTableSelectionOptions = rowSelectionProps
    ? {allowRowSelection: true, ...selectionStateHookOutput}
    : {allowRowSelection: false};

  const additionalOverflowRenderFunctions = (additionalOverflowOptions ?? []).map(({title, ...optionProps}) =>
    // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
    () => (
      <Option key={`additionalOverflowOption-${title}`} {...optionProps}>
        {title}
      </Option>
    )
  );

  if (rowSelectionProps && numSelectedRows > 0) {
    const flagRowButtons = [
      getRenderFlagRowsButtonFn((rowIds: Set<string>) => {
        rowSelectionProps.onSelectRows(rowIds, true);
      }),
      getRenderClearFlagRowsButtonFn((rowIds: Set<string>) => {
        rowSelectionProps.onSelectRows(rowIds, false);
      }, rowSelectionProps.flaggedRows),
    ];
    additionalElements = flagRowButtons.concat(additionalElements);
  }
  const toolbar = (
    <div className="u-vertical-center-items">
      <DataFrameActionToolbar
        dataFrame={sortedDataFrame}
        params={{
          dataFrameId,
          actionOrigin,
          selectedRowIds: selectionStateHookOutput.selectedRowIds,
          tableMode,
          setTableMode,
        }}
        renderElements={[...additionalElements, 'OVERFLOW_MENU']}
        overflowMenu={[
          [
            ...additionalOverflowRenderFunctions,
            renderCopyToClipboardOption,
            renderDownloadAsCSVOption,
            renderToggleChipsOption,
            ...(IS_ADD_TO_WORKLIST_ENABLED ? [renderAddToWorklistOption] : []),
          ],
        ]}
      />
    </div>
  );

  const additionalFilters =
    headerMenuOptions?.filterOptions?.getFiltersWithMetadata({}).map((filterWithMetadata) => ({
      disabled: false,
      filterWithMetadata,
    })) ?? [];
  const filterPillList = headerMenuOptions?.filterOptions && (
    <FilterPillList
      filterOptions={headerMenuOptions.filterOptions}
      readOnlyInfo={headerMenuOptions.readOnlyInfo}
      additionalFilters={additionalFilters}
    />
  );

  return (
    <div className={css(styles.paneBlock)}>
      <div className={css(styles.pane)}>
        <Pane
          title={title ?? dataFrame.name}
          toolbar={toolbar}
          banner={
            !isExpanded || (banner == null && filterPillList == null) ? null : (
              <div className={css(styles.tableBanner)}>
                {banner}
                {filterPillList}
              </div>
            )
          }
        >
          {isExpanded ? (
            <DataFrameTable
              dataFrameId={dataFrameId}
              actionOrigin={actionOrigin}
              clientStateId={clientStateId}
              dataFrame={sortedDataFrame}
              headerMenuOptions={headerMenuOptions}
              mode={tableMode}
              renderIsExcludedColumn={renderIsExcludedColumn}
              selectionOptions={selectionOptions}
              sortOptions={sortOptions}
              dataFrameRenderOverrides={dataFrameRenderOverrides}
            />
          ) : null}
        </Pane>
      </div>
      {isCollapsible && (
        <div className={css(styles.chevron)}>
          <Tooltip
            text={
              isInitiallyCollapsed && !isExpanded
                ? 'Dataset is collapsed by default due to the high number of rows. Click to expand.'
                : null
            }
          >
            <CollapserChevron
              isExpanded={isExpanded}
              name="table"
              onToggleCollapsed={() => {
                setIsExpanded(!isExpanded);
              }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  chevron: {
    marginLeft: 4,
    marginTop: 16,
  },
  pane: {
    flexGrow: 1,
    minWidth: 0,
  },
  paneBlock: {
    display: 'flex',
    maxHeight: '100%',
  },
  tableBanner: {
    display: 'grid',
    rowGap: 8,
    overflowWrap: 'anywhere',
  },
  rowSelectionStatus: {
    color: GRAY_DARKER,
  },
});
